import React from 'react';
import { motion } from 'framer-motion';
import { 
  Droplets, 
  VideoIcon, 
  Wrench, 
  Waves, 
  Zap, 
  Construction, 
  ShieldCheck, 
  Building2,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

export default function Services() {
  const services = [
    {
      icon: Droplets,
      title: 'Drain Unblocking',
      description: 'Professional drain unblocking using advanced high-pressure water jetting and mechanical tools for all types of blockages.',
      features: ['Kitchen & Bathroom Drains', 'Main Sewer Lines', 'Storm Drains', 'Root Removal'],
      image: 'https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=600&h=400&fit=crop'
    },
    {
      icon: Wrench,
      title: 'Drain Repair',
      description: 'Complete drain repair solutions using modern trenchless technology with minimal disruption to your property.',
      features: ['Trenchless Repairs', 'Pipe Relining', 'Leak Detection', 'Expert Fixes'],
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop'
    },
    {
      icon: Waves,
      title: 'Sewer Line Services',
      description: 'Expert sewer line repairs, replacement, and cleaning with professional equipment and proven techniques.',
      features: ['Sewer Cleaning', 'Line Replacement', 'Root Barrier Installation', 'Professional Diagnostics'],
      image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=600&h=400&fit=crop'
    },
    {
      icon: Zap,
      title: 'Water Line Repair',
      description: 'Fast and reliable water line repairs and replacements. We fix leaks and restore your water supply efficiently.',
      features: ['Leak Repairs', 'Pipe Replacement', 'Pressure Testing', 'Quality Materials'],
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=600&h=400&fit=crop'
    },
    {
      icon: Construction,
      title: 'Drain Installation',
      description: 'Professional drain installation for new construction and renovations with quality materials and expert workmanship.',
      features: ['New Construction', 'System Design', 'Code Compliance', 'Quality Assurance'],
      image: 'https://images.unsplash.com/photo-1607400201889-565b1ee75f8e?w=600&h=400&fit=crop'
    },
    {
      icon: ShieldCheck,
      title: 'Maintenance Plans',
      description: 'Regular maintenance programs to keep drains flowing smoothly and prevent costly repairs.',
      features: ['Annual Inspections', 'Scheduled Cleaning', 'Maintenance Plans', 'Priority Service'],
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop'
    },
    {
      icon: Building2,
      title: 'Commercial Solutions',
      description: 'Specialized drainage solutions for commercial properties from restaurants to office buildings.',
      features: ['Restaurant Grease Traps', 'Office Buildings', 'Retail Spaces', 'Industrial Sites'],
      image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&h=400&fit=crop'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <p className="text-white/60 font-medium mb-4 tracking-wide uppercase text-sm">What We Offer</p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-4 leading-tight">
              Professional Drainage
              <span className="block font-semibold">Services</span>
            </h1>
            <p className="text-slate-400 max-w-3xl mx-auto">
              Comprehensive drainage solutions for residential and commercial properties. Quality workmanship backed by years of experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-white border-l-4 border-slate-900 shadow-sm hover:shadow-md transition-shadow overflow-hidden h-full">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-slate-900 flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed mb-6 text-sm">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-slate-900 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Button */}
                    <button className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-5 transition-colors">
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
            className="text-center mb-16"
          >
            <p className="text-slate-600 font-medium mb-4 tracking-wide text-sm uppercase">Our Process</p>
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              How We
              <span className="block font-semibold">Work</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our streamlined process ensures efficient service delivery from start to finish.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Contact', desc: 'Call or message us anytime' },
              { step: '02', title: 'Assessment', desc: 'Quick diagnosis of the issue' },
              { step: '03', title: 'Solution', desc: 'Expert repair or service' },
              { step: '04', title: 'Follow-up', desc: 'Ensure complete satisfaction' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-slate-900 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-light text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}